import { onDestroy } from 'svelte'
import { handleFormSteps, handleFormErrors, handleFormComplete } from "./analytics_tools";

export function onTimeout (callback, milliseconds) {
  const timeout = setTimeout(callback, milliseconds)
  onDestroy(() => {
    clearTimeout(timeout)
  })
}

export const stringContains = string => substring =>
  string ? string.indexOf(substring) !== -1 : false

export const updateDataLayer = (options) => (e) => {
  e?.preventDefault();
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  const { event, name, step, ...rest } = options;
  let time = new Date().toISOString();
  let type = rest.type ?? "form";
  let value = rest.value ?? 0.00;
  let errors = rest.errors ?? null;
  let desc = rest.desc ?? "";
  if (event === 'formError') {
    handleFormErrors({ name, step, errors, type })
  }
  if (event === 'formComplete') {
    handleFormComplete({ name, type, desc, value })
  }
  if (event !== 'formComplete' && event !== 'formError' && step !== 'complete') {
    handleFormSteps(name, step, desc, null, value, type);
  }
}

export const webfetch = function (url, options) {
  // return fetch(url, options);
  const fetchRes = async (url, options) => {
    try {
      let res = await fetch(url, options)
      if (!res.ok) {
        throw new Error(res.statusText)
      }
      let data = await res.json()
      return data
    } catch (err) {
      throw new Error(err)
    }
  }
  return fetchRes(url, options)
}

export const msToTime = duration => {
  let hours = Math.floor((duration / (1000 * 60 * 60)) % 24)
  let days = Math.floor(duration / (1000 * 60 * 60 * 24))
  return { hours, days }
}

// export const updateGtm = args => {
//   if (window && window.digitalData) {
//     window.digitalData.tool = { name: args.tool }
//     if (args.step) {
//       window.digitalData.tool.step = args.step
//     }
//     dataLayer.push({ event: args.event })
//   }
// }

// export const updateGtmWithResult = args => {
//   if (window && window.digitalData) {
//     window.digitalData.tool = { name: args.name, result: args.result };
//     if (args.step) {
//       window.digitalData.tool.step = args.step;
//     }
//     dataLayer.push({ event: args.event })
//   }
// }

export const keyCodes = {
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  pgup: 33,
  pgdown: 34,
  enter: 13,
  escape: 27,
  tab: 9
}

export const keyCodesArray = Object.keys(keyCodes).map(k => keyCodes[k])

const getCalendarPage = (month, year, dayProps, weekStart = 0) => {
  let date = new Date(year, month, 1)
  // Check if the start date is less than the week start
  if (date.getDay() < weekStart) {
    date.setDate(date.getDate() - (7 - (weekStart - date.getDay())));
  }
  else {
    date.setDate(date.getDate() - date.getDay() + weekStart)
  }
  let nextMonth = month === 11 ? 0 : month + 1
  // ensure days starts on Sunday
  // and end on saturday
  let weeks = []
  while (
    date.getMonth() !== nextMonth ||
    date.getDay() !== weekStart ||
    weeks.length !== 6
  ) {
    if (date.getDay() === weekStart)
      weeks.unshift({ days: [], id: `${year}${month}${year}${weeks.length}` })
    const updated = Object.assign(
      {
        partOfMonth: date.getMonth() === month,
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        date: new Date(date)
      },
      dayProps(date)
    )
    weeks[0].days.push(updated)
    date.setDate(date.getDate() + 1)
  }
  weeks.reverse()
  return { month, year, weeks }
}

const getDayPropsHandler = (start, end, selectableCallback) => {
  let today = new Date()
  today.setHours(0, 0, 0, 0)
  return date => {
    const isInRange = date >= start && date <= end
    return {
      isInRange,
      selectable:
        isInRange && (!selectableCallback || selectableCallback(date)),
      isToday: date.getTime() === today.getTime()
    }
  }
}

export function getMonths(
  start,
  end,
  selectableCallback = null,
  weekStart = 0
) {
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  let endDate = new Date(end.getFullYear(), end.getMonth() + 1, 1)
  let months = []
  let date = new Date(start.getFullYear(), start.getMonth(), 1)
  let dayPropsHandler = getDayPropsHandler(start, end, selectableCallback)
  while (date < endDate) {
    months.push(
      getCalendarPage(
        date.getMonth(),
        date.getFullYear(),
        dayPropsHandler,
        weekStart
      )
    )
    date.setMonth(date.getMonth() + 1)
  }
  return months
}

export const areDatesEquivalent = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear()

/**
 * @returns {array} [int month, int day]
 */
export function getEaster(year) {
  let f = Math.floor,
    // Golden Number - 1
    G = year % 19,
    C = f(year / 100),
    // related to Epact
    H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
    // number of days from 21 March to the Paschal full moon
    I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
    // weekday for the Paschal full moon
    J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
    // number of days from 21 March to the Sunday on or before the Paschal full moon
    L = I - J,
    month = 3 + f((L + 40) / 44),
    day = L + 28 - 31 * f(month / 4)

  let Sunday = new Date(year, month - 1, day, 0, 0, 0, 0)
  let Monday = new Date(new Date(Sunday).setDate(Sunday.getDate() + 1))
  let Friday = new Date(new Date(Sunday).setDate(Sunday.getDate() - 2))

  return [Monday, Friday]
}

export function getFirstMonday(year, month) {
  let d = new Date(year, month, 1, 0, 0, 0)
  while (d.getDay() !== 1) {
    d.setDate(d.getDate() + 1)
  }
  return d
}

function scrollTo(element, to, duration, onDone) {
  let start = element.scrollTop,
    change = to - start,
    startTime = performance.now(),
    val,
    now,
    elapsed,
    t

  function animateScroll() {
    now = performance.now()
    elapsed = (now - startTime) / 1000
    t = elapsed / duration

    element.scrollTop = start + change * easeInOutQuad(t)

    if (t < 1) window.requestAnimationFrame(animateScroll)
    else onDone && onDone()
  }

  animateScroll()
}

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}
function getRelativePos(elm) {
  let pPos = elm.parentNode.getBoundingClientRect(), // parent pos
    cPos = elm.getBoundingClientRect(), // target pos
    pos = {}

    ; (pos.top = cPos.top - pPos.top + elm.parentNode.scrollTop),
      (pos.right = cPos.right - pPos.right),
      (pos.bottom = cPos.bottom - pPos.bottom),
      (pos.left = cPos.left - pPos.left)

  return pos
}

export function scrollToElm(container, elm, duration) {
  let pos = getRelativePos(elm)
  scrollTo(container, pos.top - 150, 0.1) // duration in seconds
}

export function debounce(func, wait, immediate) {
  let timeout

  return function executedFunction() {
    let context = this
    let args = arguments

    let later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    let callNow = immediate && !timeout

    clearTimeout(timeout)

    timeout = setTimeout(later, wait)

    if (callNow) func.apply(context, args)
  }
}

export function clickOutside(node) {
  var handleClick = function handleClick(event) {
    if (node && !node.contains(event.target) && !event.defaultPrevented) {
      node.dispatchEvent(new CustomEvent('click_outside', node));
    }
  };

  document.addEventListener('click', handleClick, true);
  return {
    destroy: function destroy() {
      document.removeEventListener('click', handleClick, true);
    }
  };
}

export const convertToNumberString = (num) => {
  let numArray = num.toString().split(".");
  let wholeNumber = numArray[0]
    .split("")
    .reverse()
    .map((str, i) => (i !== 0 && i % 3 === 0 ? str + "," : str))
    .reverse()
    .join("");
  return numArray[1] ? `${wholeNumber}.${numArray[1]}` : wholeNumber;
};

export const convertToMoney = (num) => {
  let round = Math.round(num * 100) / 100;
  let moneyArray = round.toString().split(".");
  let dollars = moneyArray[0]
    .split("")
    .reverse()
    .map((str, i) => (i !== 0 && i % 3 === 0 ? str + "," : str))
    .reverse()
    .join("");
  return `$${dollars}.${moneyArray[1]
    ? moneyArray[1].length === 2
      ? moneyArray[1]
      : String(moneyArray[1]) + "0"
    : "00"
    }`;
};

export function once(fn) {
  return function (event) {
    if (fn) fn.call(this, event);
    fn = null;
  };
}